// Nurse1PatientQueue.tsx
import React, { useEffect, useMemo, useState } from "react";
import api from "../../../api/axiosConfig";
import "../../../center admin css/centerAdmin.css";

/** ====== Center Workflow response types ====== */
type WaitingItem = {
  _id: string;
  appointmentId?: string;
  stationId: string;
  sessionId?: string;
  pathOrder?: number;
  createdAt: string;
  state?: string;
  startedAt?: string;
  child: {
    name: string;
    age: number;
    gender: string;
    emrId: string;
    childId: string;
  };
  pathId?: string;
  sessionStartAt?: string;
};

type StationPatientsResp = {
  waiting: WaitingItem[];
  inProgress: WaitingItem[];
  completed: WaitingItem[];
};

type QueueItem = {
  id: string;
  emrId: string;
  date: string;
  name: string;
  genderShort: "M" | "F";
  ageYears: number;
  avatarUrl?: string;
  profileKey?: string;
  time?: string;
  _source?: "IN_QUEUE" | "WAITING" | "IN_PROGRESS" | "NO_CHILD";
  state?: "IN_QUEUE" | "WAITING" | "IN_PROGRESS" | "NO_CHILD";
  isEmpty?: boolean;
  label?: string;
  sessionId?: string;
  pathOrder?: number;
  slotRow?: number;
  rowIndex?: number;
};

type Props = {
  centerId: string;
  dateISO?: string;
  activeId?: string;
  onSelect?: (item: WaitingItem) => void;
  autoRefreshMs?: number;
  stationId?: string;
  queueApi?: string;
  preserveMatrixOrder?: boolean;
};

function formatDmy(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

function formatHm(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

const Nurse1PatientQueue: React.FC<Props> = ({
  centerId,
  dateISO,
  activeId,
  onSelect,
  autoRefreshMs,
  stationId = "NURSE1",
  queueApi = "NURSE1_Q",
  preserveMatrixOrder = false,
}) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [items, setItems] = useState<QueueItem[]>([]);
  const [rawInProgress, setRawInProgress] = useState<WaitingItem[]>([]);
  const [rawInQueue, setRawInQueue] = useState<WaitingItem[]>([]);

  // Stores emrId -> presignedUrl
  const [avatarMap, setAvatarMap] = useState<Record<string, string>>({});

  const effectiveDate = dateISO || todayISO();

  const getCenterId = () => {
    try {
      const rawConfig = sessionStorage.getItem("configResponse");

      if (!rawConfig) return "";

      const parsedConfig = JSON.parse(rawConfig);

      return parsedConfig?.data?.[0]?.centerId || "";
    } catch (error) {
      console.error("Failed to read configResponse:", error);
      return "";
    }
  };

  /** ── queue loader ── */
  async function load() {
    if (items.length === 0) setLoading(true);
    setErr(null);

    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) throw new Error("No access token found. Please log in.");

      const headers = { Authorization: `Bearer ${token}` };

      const currentCenterId = getCenterId();

      if (!currentCenterId) throw new Error("Center ID not found");

      const releasedSlotId = sessionStorage.getItem("releasedSlotId") || "";

      const { data } = await api.centerworkflowApi.get(
        `/queues/${queueApi}?centerId=${encodeURIComponent(
          currentCenterId,
        )}&date=${encodeURIComponent(
          effectiveDate,
        )}&releasedSlotId=${encodeURIComponent(releasedSlotId)}`,
        { headers },
      );

      const matrixRows = Array.isArray(data?.matrixRows) ? data.matrixRows : [];

      const inQueue = Array.isArray(data?.inQueue) ? data.inQueue : [];

      const inProgress = Array.isArray(data?.inProgress) ? data.inProgress : [];

      setRawInQueue(inQueue);
      setRawInProgress(inProgress);

      const mapped: QueueItem[] = matrixRows

        .filter(
          (row: any) =>
            row.state === "IN_QUEUE" ||
            row.state === "WAITING" ||
            row.state === "IN_PROGRESS" ||
            row.state === "NO_CHILD",
        )
        .map((row: any) => {
          const item = row.item;

          const ts = item?.startedAt || item?.sessionStartAt || item?.createdAt;

          return {
            id: item?._id || `empty-${row.slotRow}`,

            emrId: row.child?.childId || "",

            date: formatDmy(ts),

            time: formatHm(ts),

            label:
              row?.scheduledTime?.label ||
              row?.item?.scheduledTime?.label ||
              row?.label ||
              row?.slotLabel ||
              row?.slot_label ||
              "",

            name:
              row.state === "NO_CHILD"
                ? "No Patient"
                : row.child?.name || "Unknown",

            genderShort: String(row.child?.gender || "M")
              .toUpperCase()
              .startsWith("F")
              ? "F"
              : "M",

            ageYears: Number(row.child?.age ?? 0),

            avatarUrl: undefined,

            profileKey:
              row.child?.profile ||
              item?.child?.profile ||
              item?.profile ||
              undefined,

            _source: row.state,

            state: row.state,

            isEmpty: row.state === "NO_CHILD",

            sessionId: row.sessionId,

            pathOrder: row.pathOrder || 0,

            slotRow: row.slotRow || 0,

            rowIndex: row.row || 0,
          };
        });

      const sortedMapped = [...mapped].sort(
        (a, b) => (a.rowIndex || 0) - (b.rowIndex || 0),
      );

      setItems(sortedMapped);

      const hasActive = activeId && mapped.some((m) => m.id === activeId);

      if (!hasActive) {
        const selectedPatient =
          inProgress[0] || inQueue.find((x: any) => x.state === "IN_QUEUE");

        if (selectedPatient) {
          onSelect?.(selectedPatient);
        } else {
          onSelect?.(null as any);
        }
      }

      await fetchImages(mapped, headers);
    } catch (e: any) {
      setErr(e?.message || "Failed to fetch station queue");
    } finally {
      setLoading(false);
    }
  }

  /* ------------------ 🔐 PRESIGNED IMAGE FETCH (FIXED) ------------------ */
  // async function fetchImages(queueItems: QueueItem[], headers: any) {
  //   const currentCenterId = getCenterId();
  //   if (!currentCenterId) return;

  //   try {
  //     const apptRes = await api.appointment_api.get(
  //       `/api/appointments/center/${encodeURIComponent(
  //         currentCenterId,
  //       )}/date?date=${encodeURIComponent(effectiveDate)}`,
  //       { headers },
  //     );

  //     const slots = apptRes.data?.slots || [];

  //     const bookingSlotMap: Record<string, string> = {};

  //     slots.forEach((slot: any) => {
  //       const label = slot.slot_label;

  //       (slot.bookings || []).forEach((booking: any) => {
  //         if (booking.child_id) {
  //           bookingSlotMap[booking.child_id] = label;
  //         }
  //       });
  //     });

  //     const bookings = slots.flatMap((slot: any) => slot.bookings || []);

  //     const newMap: Record<string, string> = {};

  //     for (const b of bookings) {
  //       const filePath = b.child_profile;
  //       if (!filePath || !b.child_id) continue;

  //       // Skip if we already have this image
  //       if (avatarMap[b.child_id]) {
  //         newMap[b.child_id] = avatarMap[b.child_id];
  //         continue;
  //       }

  //       try {
  //         // If already presigned, use as is
  //         if (filePath.includes("X-Amz-Signature")) {
  //           newMap[b.child_id] = filePath;
  //           continue;
  //         }

  //         // ✅ FIX: Manual string splitting instead of new URL()
  //         const parts = filePath.split("/");

  //         // Check structure
  //         if (parts.length < 3) continue;

  //         // Extract Module (first two segments)
  //         const module = `${parts[0]}/${parts[1]}`;

  //         // Extract Key (remainder)
  //         const key = parts.slice(2).join("/");

  //         const img = await api.S3config.get("/view-url", {
  //           params: { module, key },
  //           headers,
  //         });

  //         // Robust response check
  //         const signedUrl = img.data?.url || (img as any).url;
  //         if (signedUrl) {
  //           newMap[b.child_id] = signedUrl;
  //         }
  //       } catch (e) {
  //         // ignore failures
  //       }
  //     }

  //     setAvatarMap((prev) => ({ ...prev, ...newMap }));

  //     setItems((prev) =>
  //       [...prev].sort((a, b) => (a.rowIndex || 0) - (b.rowIndex || 0)),
  //     );
  //   } catch (e) {
  //     console.warn("Avatar load failed", e);
  //   }
  // }
  ///* ------------------ ------------------ */

  /* ------------------ PRESIGNED IMAGE FETCH ------------------ */
  async function fetchImages(queueItems: QueueItem[], headers: any) {
    try {
      const newMap: Record<string, string> = {};

      for (const item of queueItems) {
        const profileKey = item.profileKey;

        if (!profileKey || !item.emrId) continue;

        // Already a signed URL
        if (profileKey.includes("X-Amz-Signature")) {
          newMap[item.emrId] = profileKey;
          continue;
        }

        const parts = profileKey.split("/");

        if (parts.length < 3) {
          console.warn("Invalid profile key:", profileKey);
          continue;
        }

        // Example:
        // checkin/capture-photo/P-2026-0000113-0/filename.jpg

        const module = `${parts[0]}/${parts[1]}`;
        const key = parts.slice(2).join("/");

        try {
          const response = await api.S3config.get("/view-url", {
            params: {
              module,
              key,
            },
            headers,
          });

          const signedUrl = response.data?.url || (response as any).url;

          if (signedUrl) {
            newMap[item.emrId] = signedUrl;
          }
        } catch (err) {
          console.warn(`Failed to generate image URL for ${item.emrId}`, err);
        }
      }

      setAvatarMap((prev) => ({
        ...prev,
        ...newMap,
      }));
    } catch (err) {
      console.warn("Avatar load failed", err);
    }
  }
  /* ------------------------------------------------------------ */

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId]);

  useEffect(() => {
    const handlePatientUpdated = async () => {
      await load();
    };

    window.addEventListener("patientUpdated", handlePatientUpdated);

    return () => {
      window.removeEventListener("patientUpdated", handlePatientUpdated);
    };
  }, []);

  useEffect(() => {
    if (!autoRefreshMs) return;
    const t = setInterval(load, autoRefreshMs);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId, autoRefreshMs, items.length]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    // Merge avatar map into items
    const withImages = items.map((i) => ({
      ...i,
      // Ensure whitespace doesn't break matching
      avatarUrl: avatarMap[i.emrId] || avatarMap[i.emrId.trim()] || i.avatarUrl,
    }));

    if (!q) return withImages;
    return withImages.filter((p) => p.name.toLowerCase().includes(q));
  }, [items, query, avatarMap]);

  return (
    <aside className="gc-queue">
      <div className="gc-queue-header">
        <div className="gc-queue-title">
          Patient Queue
          <span className="gc-queue-underline" />
        </div>

        <div className="gc-queue-searchwrap">
          <input
            className="gc-queue-search"
            placeholder="Search patients..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="gc-queue-list">
        {loading && items.length === 0 && (
          <div className="gc-queue-empty">Loading…</div>
        )}
        {err && items.length === 0 && (
          <div className="gc-queue-empty">Error: {err}</div>
        )}
        {!loading && !err && filtered.length === 0 && (
          <div className="gc-queue-empty">No patients</div>
        )}

        {filtered.map((p) => {
          const active = p.id === activeId;
          return (
            <button
              key={p.id}
              className={`gc-queue-item ${active ? "active" : ""} ${
                p._source === "IN_QUEUE" || p._source === "IN_PROGRESS"
                  ? "in-progress"
                  : p._source === "NO_CHILD"
                    ? "no-child"
                    : "waiting"
              }`}
              onClick={() => {
                if (p._source === "IN_QUEUE" || p._source === "IN_PROGRESS") {
                  const selectedPatient =
                    rawInQueue.find((x) => x._id === p.id) ||
                    rawInProgress.find((x) => x._id === p.id);

                  if (selectedPatient) {
                    onSelect?.(selectedPatient);
                    console.log("QUEUE CLICKED:", selectedPatient);
                  }
                }
              }}
              disabled={p._source !== "IN_QUEUE" && p._source !== "IN_PROGRESS"}
              style={{
                cursor:
                  p._source === "IN_QUEUE" || p._source === "IN_PROGRESS"
                    ? "pointer"
                    : "not-allowed",

                opacity:
                  p._source === "IN_QUEUE" || p._source === "IN_PROGRESS"
                    ? 1
                    : 0.6,
              }}
              title={p.time ? `Time: ${p.time}` : undefined}
            >
              <div className="gc-queue-item-body">
                <div className="gc-queue-avatar">
                  {p.avatarUrl ? (
                    <img src={p.avatarUrl} alt={p.name} />
                  ) : (
                    <div className="gc-queue-avatar-fallback" />
                  )}
                </div>

                <div className="gc-queue-item-info">
                  <div className="gc-queue-item-date">
                    {p.label ? `${p.label} · ${p.date}` : p.date}
                  </div>

                  <div className="gc-queue-name">
                    {p.isEmpty ? "No Child" : p.name}
                  </div>

                  <div className="gc-queue-sub">
                    {p.isEmpty
                      ? "Waiting for Child"
                      : `${p.genderShort}, ${p.ageYears} ${
                          p.ageYears === 1 ? "Year" : "Years"
                        }`}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="gc-queue-footer">
        <div className="gc-queue-meta">
          <small>
            Station: <b>{stationId}</b> · Date: <b>{effectiveDate}</b>
          </small>
        </div>
      </div>
    </aside>
  );
};

export default Nurse1PatientQueue;
